<template>

  <v-container fluid class="pa-0 ma-0 asset-uploader">
    <!-- File selector -->
    <v-row class="pa-0 ma-0">
      <v-col class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <v-file-input
            class="image-upload-input-area"
            :accept="allowedFileTypes"
            prepend-icon="mdi-file-image"
            :label="label"
            @click:clear="clear"
            @change="onChangeInput" />
      </v-col>
    </v-row>

    <!-- Rendered upload -->
    <v-row class="pa-0 ma-0" v-if="file">
      <v-col class="pa-0 ma-0">
        <div class="file-container">
          <embed
              v-if="isDoc"
              :height="renderHeight"
              :src="docSrc" />
          <v-img
              v-else
              contain
              max-width="100%"
              :max-height="renderHeight"
              :src="fileData" />
        </div>
      </v-col>
    </v-row>

    <!-- Actions -->
    <v-row class="pa-0 ma-0 mt-3">
      <v-col style="text-align:center" class="pa-0 ma-0">
        <v-btn
            v-if="showCancel && !isUploading"
            text
            width="100"
            @click="cancel">
          Cancel
        </v-btn>
        <v-btn
            v-if="file && !isUploading"
            text
            width="100"
            @click="clear">
          Clear
        </v-btn>
        <div v-if="isUploading">
          <v-progress-circular indeterminate :size="18" color="primary" class="mr-2" />
          <span>Uploading...</span>
        </div>
        <v-btn
            v-else
            text
            color="primary"
            width="100"
            :disabled="!file"
            @click="save">
          Save
        </v-btn>
      </v-col>
    </v-row>
  </v-container>

</template>

<script>
export default {
  name: 'AssetUploader',

  props: {
    assetType: {
      type: String,
      required: true
    },
    allowedFileTypes: {
      type: String,
      default: 'application/pdf, image/jpeg, image/png, image/webp'
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: 'Upload'
    },
    renderHeight: {
      type: Number,
      default: 400
    },
    // ---------------- Upload options
    enableUpload: {
      type: Boolean,
      default: true
    },
    assetUploadUri: {
      type: String,
      default: 'my/user/asset/uploadUrl'
    },
    // ----------------- Resize image options
    resizeImage: {
      type: Boolean,
      default: true
    },
    imageMaxHeight: {
      type: Number,
      default: 960
    },
    imageMaxWidth: {
      type: Number,
      default: 960
    }
  },

  data () {
    return {
      file: null,
      fileName: null,
      fileType: null,
      fileExtension: null,
      fileData: null,
      isDoc: false,
      isUploading: false
    }
  },

  computed: {
    docSrc () {
      // NOTES: #toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0
      return (this.fileData) ? `${this.fileData}#toolbar=0&navpanes=0&scrollbar=0` : null
    }
  },

  beforeDestroy () {
    this.clear()
  },

  methods: {
    // Handler for file change
    onChangeInput (file) {
      if (!file) return false

      return new Promise((resolve, reject) => {
        // Save file and process data for preview
        this.file = file
        this.fileName = file.name
        this.fileType = file.type
        this.fileExtension = file.name.substring((file.name.lastIndexOf('.') + 1))

        const reader = new FileReader()
        reader.onload = async (e) => {
          await this.processFile(e)
          return resolve(reader.result)
        }
        reader.onerror = reject
        reader.readAsDataURL(this.file)

        return resolve(true)
      })
    },

    // Clear active state
    clear () {
      this.file = null
      this.fileName = null
      this.fileType = null
      this.fileExtension = null
      this.fileData = null
      this.isDoc = false
    },

    // Emit canceled event and reset state
    cancel () {
      this.clear()
      this.$emit('canceled')
    },

    // Process added file
    async processFile (event) {
      if (event) {
        this.fileData = event.target.result
        this.renderUpload(this.fileData)
      }
    },

    // Render uploaded file
    async renderUpload (data) {
      if (!data) return false

      // Extract doc type (only works for new file attachment)
      const docType = this.fileData.split(';')[0].substring(5)

      // Render file per configuration
      if (docType.startsWith('image/')) {
        this.isDoc = false
        // Resize image if enabled
        if (this.resizeImage) {
          const resizedFile = await this.resizeImageData()
          if (resizedFile) this.file = resizedFile
        }
      // Otherwise presume a doc (e.g. PDF)
      } else {
        this.isDoc = true
      }
    },

    // Returns the provided image data as a resized file object
    async resizeImageData () {
      const maxWidth = this.imageMaxWidth
      const maxHeight = this.imageMaxHeight
      const imageData = this.fileData
      const imageType = this.fileType
      const imageName = this.fileName
      if (!imageData) return false

      // Generate image to determine reduced size
      const originalImage = await this.createImage(imageData)

      return new Promise((resolve) => {
        // Generate new dimensions
        const aspectRatio = originalImage.width / originalImage.height
        let width = originalImage.width
        let height = originalImage.height

        if (width > maxWidth) {
          width = maxWidth
          height = width / aspectRatio
        }
        if (height > maxHeight) {
          height = maxHeight
          width = height * aspectRatio
        }

        // Render element to build file object
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(originalImage, 0, 0, width, height)
        canvas.toBlob(
          (blob) => {
            const file = new File([blob], imageName, { type: imageType })
            resolve(file)
          },
          imageType,
          0.9
        )
      })
    },

    // Create image helper for performing a resize
    async createImage (imageData) {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          resolve(img)
        }
        img.src = imageData
      })
    },

    // Emit saved file info and perform upload (if enabled)
    async save () {
      let uploaded = false

      // Perform upload, if enabled
      if (this.enableUpload) {
        this.isUploading = true
        try {
          await this.uploadAsset()
          uploaded = true
        } catch (err) {
          console.log('Error uploading asset =>', err)
        }
        this.isUploading = false
      }

      // Emit file data and meta
      this.$emit('saved', {
        asset_type: this.assetType,
        file_extension: this.fileExtension,
        // asset_key: `${this.assetType}.${this.fileExtension}`,
        asset_key: this.assetType,
        file: this.file,
        image_data: this.fileData,
        uploaded
      })
    },

    // Perform upload of asset via S3 signed upload URL
    async uploadAsset () {
      const assetType = this.assetType
      const file = this.file
      if (!assetType || !file) return false

      let uploadInfo = null
      try {
        // Generate upload URL
        uploadInfo = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: this.assetUploadUri,
          opts: {
            params: {
              asset_key: assetType
              // asset_type: assetType
              // file_extension: this.fileExtension
            }
          },
          to_model: false
        })

        // Upload document
        await fetch(uploadInfo.url, { method: 'PUT', body: file })
      } catch (err) {
        console.log(`Error generating upload info for user asset: ${assetType} =>`, err)
        throw err
      }
    }
  }
}
</script>

<style scoped>
  .asset-uploader .file-container {
    border: 1px solid #525252;
    padding: 0;
    min-height: 20px;
    text-align: center;
  }
</style>
